import ButtonBase from '@ixigo/iui/ButtonBase';
import { Skeleton } from '@repo/shared/components';
import { useEffect, useState } from 'react';
import { type CabUpdateResponse, getCabUpdates } from '@/api';
import { useNavigate } from '@/lib/navigation';
import { useCabUpdatesPolling, useTripActivation } from '../cab-update/hooks';
import type { CabUserBooking } from './api';
import { RideDetail } from './ride-detail';
import { RouteInfo } from './route-info';
import { Status } from './status';
import { transformBookingInfo } from './utils';

interface UpcomingBookingCardProps {
	bookingInfo: CabUserBooking;
	loading: boolean;
}

export const Card = ({ bookingInfo, loading }: UpcomingBookingCardProps) => {
	const { bookingId } = bookingInfo;
	const navigate = useNavigate();
	const [cabUpdate, setCabUpdate] = useState<CabUpdateResponse | null>(null);

	useEffect(() => {
		const fetchCabUpdate = async () => {
			const response = await getCabUpdates(bookingId);
			if (!('errors' in response)) {
				const cabUpdateData = response.data;
				setCabUpdate(cabUpdateData);
			}
		};
		fetchCabUpdate();
	}, [bookingId]);

	const { isTripActive } = useTripActivation({
		tripActivationTime: cabUpdate?.tripActivationTime.time || '',
		pollingIntervalMs: cabUpdate?.pollingIntervalMs,
	});
	const { cabUpdate: pollingCabUpdate } = useCabUpdatesPolling({
		bookingId,
		isTripActive,
		pollingIntervalMs: cabUpdate?.pollingIntervalMs || 0,
	});

	const handleClick = () => {
		navigate({
			to: '/trips/$bookingId',
			params: {
				bookingId,
			},
			viewTransition: {
				types: ['slide-left'],
			},
		});
	};

	const { bookingDetail, rideDetails } = transformBookingInfo(bookingInfo);
	const syncedCabUpdate = pollingCabUpdate || cabUpdate;
	return (
		<ButtonBase
			className="flex flex-col bg-primary overflow-hidden w-full h-full"
			onClick={handleClick}
		>
			<div className="trip-info flex flex-col gap-2.5 p-10 pb-5">
				<RideDetail
					loading={loading}
					rideDetails={rideDetails}
					bookingStatus={bookingDetail.bookingStatus}
					rideStatus={syncedCabUpdate?.rideStatus}
				/>
				<RouteInfo rideDetails={rideDetails} loading={loading} />
			</div>
			<div className="flex items-center gap-2 border-t border-tertiary px-10 py-5">
				<Skeleton
					loading={loading}
					variant="circular"
					className="w-full"
					level="body-xs"
					height={16}
				>
					{bookingDetail && (
						<Status
							bookingDetail={bookingDetail}
							cabUpdate={syncedCabUpdate}
							isTripActive={isTripActive}
						/>
					)}
				</Skeleton>
			</div>
		</ButtonBase>
	);
};
